import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaUsers, 
  FaQuestionCircle, 
  FaTrash, 
  FaEdit, 
  FaEye, 
  FaEyeSlash,
  FaSearch,
  FaFilter,
  FaEnvelope,
  FaCog,
  FaPaperPlane,
  FaInbox,
  FaGlobe,
  FaLock,
  FaChartLine,
  FaBriefcase,
  FaMapMarkerAlt,
  FaClock,
  FaFileAlt,
  FaPlus,
  FaCheck,
  FaTimes,
  FaGraduationCap,
  FaChartBar,
  FaUnlock,
  FaUserShield,
  FaRobot,
  FaShieldAlt,
  FaUserGraduate,
  FaBook
} from 'react-icons/fa';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc, query, where, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin, ADMIN_AUTH_EXPIRATION } from '../utils/adminConfig';
import SEO from './SEO';
import ChatServiceAdmin from './admin/ChatServiceAdmin';
import ModerationAdmin from './admin/ModerationAdmin';
import ExpertSupportAdmin from './admin/ExpertSupportAdmin';
import './Admin.css';

const Admin = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [emails, setEmails] = useState([]);
  const [websiteSettings, setWebsiteSettings] = useState({
    maintenanceMode: false,
    registrationEnabled: true,
    maxQuestionsPerDay: 5,
    maxFileSize: 10,
    analyticsEnabled: true
  });
  const [newEmail, setNewEmail] = useState({
    to: '',
    subject: '',
    content: ''
  });
  const [jobs, setJobs] = useState([]);
  const [newJob, setNewJob] = useState({
    title: '',
    company: '',
    location: '',
    type: 'Full-time',
    description: '',
    requirements: [],
    status: 'open'
  });
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [mockExams, setMockExams] = useState([]);
  const [showExamForm, setShowExamForm] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [newExam, setNewExam] = useState({
    title: '',
    subject: '',
    duration: 60,
    totalMarks: 100,
    questions: []
  });
  const [newQuestion, setNewQuestion] = useState({
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0
  });
  const [messages, setMessages] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    email: '',
    role: 'user',
    isActive: true
  });
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [newMessage, setNewMessage] = useState({
    recipient: '',
    content: ''
  });
  const [materials, setMaterials] = useState([]);
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [newMaterial, setNewMaterial] = useState({
    title: '',
    subject: '',
    type: 'document',
    url: '',
    description: '',
    tags: []
  });
  const [newTag, setNewTag] = useState('');

  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!currentUser) {
        console.log("No user logged in, redirecting to login page");
        navigate('/login');
        return;
      }

      // Check if this is the designated admin email
      if (!isAdmin(currentUser)) {
        console.log("User is not the designated admin, redirecting to home page");
        navigate('/');
        return;
      }

      try {
        // First check local storage for admin authentication
        const localAdminAuth = localStorage.getItem('adminAuth');
        let hasLocalAdminAccess = false;
        
        if (localAdminAuth) {
          const adminData = JSON.parse(localAdminAuth);
          if (adminData.userId === currentUser.uid && adminData.expires > Date.now()) {
            console.log("Admin access granted through local storage");
            hasLocalAdminAccess = true;
            
            // We can proceed with the admin panel
            fetchData();
            setIsLoading(false);
            
            // Try to update the user's role in the background
            try {
              const userRef = doc(db, 'users', currentUser.uid);
              await updateDoc(userRef, { 
                role: 'admin',
                updatedAt: new Date()
              });
              console.log("Successfully updated admin role in database");
            } catch (error) {
              console.warn("Could not persist admin role to database, but local admin access is enabled", error);
            }
            
            return; // Skip the rest of the checks
          } else {
            // Clean up expired admin auth
            localStorage.removeItem('adminAuth');
          }
        }
        
        // Check Firestore for admin role
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists() && userSnap.data().role === 'admin') {
          console.log("Admin access granted through database role");
          fetchData();
          setIsLoading(false);
        } else {
          // Not an admin, redirect to admin auth
          console.log("User not recognized as admin, redirecting to auth page");
          navigate('/admin-auth');
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        
        // Double-check local storage as a fallback
        const localAdminAuth = localStorage.getItem('adminAuth');
        if (localAdminAuth) {
          const adminData = JSON.parse(localAdminAuth);
          if (adminData.userId === currentUser.uid && adminData.expires > Date.now()) {
            console.log("Admin access granted through local storage (fallback)");
            fetchData();
            setIsLoading(false);
            return;
          }
        }
        
        // Redirect to the dedicated admin authentication page on error
        navigate('/admin-auth');
      }
    };

    checkAdminStatus();
  }, [currentUser, navigate]);

  const fetchData = async () => {
    try {
      // Fetch users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      setUsers(usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Fetch mock exams
      const examsSnapshot = await getDocs(collection(db, 'mockExams'));
      setMockExams(examsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Fetch jobs - combine Firestore jobs with local jobs
      const jobsSnapshot = await getDocs(collection(db, 'jobs'));
      const firestoreJobs = jobsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Get locally stored jobs
      const localJobs = JSON.parse(localStorage.getItem('localJobs') || '[]');
      
      // Combine both sources, with local jobs taking precedence for duplicates
      const allJobs = [...firestoreJobs];
      
      // Add local jobs that don't exist in Firestore
      localJobs.forEach(localJob => {
        if (!firestoreJobs.some(fJob => fJob.id === localJob.id)) {
          allJobs.push(localJob);
        }
      });
      
      setJobs(allJobs);

      // Fetch messages
      const messagesSnapshot = await getDocs(collection(db, 'messages'));
      setMessages(messagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Fetch questions
      const questionsSnapshot = await getDocs(collection(db, 'questions'));
      setQuestions(questionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Fetch settings
      const settingsDoc = await getDoc(doc(db, 'settings', 'websiteSettings'));
      if (settingsDoc.exists()) {
        setWebsiteSettings(settingsDoc.data());
      }

      // Fetch emails/contact messages
      const emailsSnapshot = await getDocs(collection(db, 'contactMessages'));
      setEmails(emailsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      // Fetch academic materials
      const materialsSnapshot = await getDocs(collection(db, 'materials'));
      setMaterials(materialsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        await deleteDoc(doc(db, 'questions', id));
        setQuestions(questions.filter(q => q.id !== id));
      } catch (error) {
        console.error('Error deleting question:', error);
      }
    }
  };

  const handleToggleVisibility = async (id, currentVisibility) => {
    try {
      await updateDoc(doc(db, 'questions', id), { isVisible: !currentVisibility });
      setQuestions(questions.map(q => 
        q.id === id ? { ...q, isVisible: !q.isVisible } : q
      ));
    } catch (error) {
      console.error('Error updating question visibility:', error);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'sentEmails'), {
        ...newEmail,
        sentBy: currentUser.uid,
        sentAt: new Date().toISOString()
      });
      setNewEmail({ to: '', subject: '', content: '' });
      alert('Email sent successfully!');
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email. Please try again.');
    }
  };

  const handleUpdateSettings = async (setting, value) => {
    try {
      const newSettings = { ...websiteSettings, [setting]: value };
      setWebsiteSettings(newSettings);
      
      // Update settings in Firestore
      await setDoc(doc(db, 'settings', 'websiteSettings'), newSettings);
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Failed to update settings. Please try again.');
    }
  };

  const handleAddJob = () => {
    setShowJobForm(true);
    setNewJob({
      title: '',
      company: '',
      location: '',
      type: 'Full-time',
      description: '',
      requirements: [],
      status: 'open'
    });
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setNewJob(job);
    setShowJobForm(true);
  };

  const handleDeleteJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        // Check if it's a local job
        if (jobId.startsWith('local_')) {
          // Delete from localStorage
          const localJobs = JSON.parse(localStorage.getItem('localJobs') || '[]');
          const updatedLocalJobs = localJobs.filter(job => job.id !== jobId);
          localStorage.setItem('localJobs', JSON.stringify(updatedLocalJobs));
          
          // Update state directly
          setJobs(jobs.filter(job => job.id !== jobId));
          alert('Local job deleted successfully');
        } else {
          // Delete from Firestore
          await deleteDoc(doc(db, 'jobs', jobId));
          fetchData();
        }
      } catch (error) {
        console.error('Error deleting job:', error);
        alert('Error deleting job: ' + error.message);
      }
    }
  };

  const handleToggleJobStatus = (id) => {
    setJobs(jobs.map(job => 
      job.id === id 
        ? { ...job, status: job.status === 'Open' ? 'Closed' : 'Open' }
        : job
    ));
  };

  const handleAddExam = () => {
    setShowExamForm(true);
    setNewExam({
      title: '',
      subject: '',
      duration: 60,
      totalMarks: 100,
      questions: []
    });
  };

  const handleEditExam = (exam) => {
    setEditingExam(exam);
    setNewExam(exam);
    setShowExamForm(true);
  };

  const handleDeleteExam = async (examId) => {
    if (window.confirm('Are you sure you want to delete this exam?')) {
      try {
        await deleteDoc(doc(db, 'mockExams', examId));
        fetchData();
      } catch (error) {
        console.error('Error deleting exam:', error);
      }
    }
  };

  const handleAddQuestion = () => {
    setNewExam(prev => ({
      ...prev,
      questions: [...prev.questions, { ...newQuestion }]
    }));
    setNewQuestion({
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0
    });
  };

  const handleSaveExam = async () => {
    try {
      if (editingExam) {
        await updateDoc(doc(db, 'mockExams', editingExam.id), newExam);
      } else {
        await addDoc(collection(db, 'mockExams'), {
          ...newExam,
          createdAt: new Date().toISOString()
        });
      }
      setShowExamForm(false);
      setEditingExam(null);
      fetchData();
    } catch (error) {
      console.error('Error saving exam:', error);
    }
  };

  const handleAddUser = async () => {
    try {
      if (editingUser) {
        // Update existing user
        await updateDoc(doc(db, 'users', editingUser.id), {
          email: newUser.email,
          role: newUser.role,
          isActive: newUser.isActive
        });
        
        setUsers(users.map(user => 
          user.id === editingUser.id ? { ...user, ...newUser } : user
        ));
      } else {
        // Add new user
        const userRef = await addDoc(collection(db, 'users'), {
          ...newUser,
          createdAt: new Date().toISOString()
        });
        
        setUsers([...users, { id: userRef.id, ...newUser }]);
      }
      
      // Reset form
      setShowUserForm(false);
      setEditingUser(null);
      setNewUser({
        email: '',
        role: 'user',
        isActive: true
      });
      
      alert(editingUser ? 'User updated successfully!' : 'User added successfully!');
    } catch (error) {
      console.error('Error adding/updating user:', error);
      alert('Error: ' + error.message);
    }
  };

  const handleEditUser = (userId) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setEditingUser(user);
      setNewUser({
        email: user.email,
        role: user.role || 'user',
        isActive: user.isActive !== false
      });
      setShowUserForm(true);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteDoc(doc(db, 'users', userId));
        fetchData();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleSendMessage = async () => {
    try {
      await addDoc(collection(db, 'messages'), {
        senderId: 'admin',
        senderName: 'System Admin',
        recipientId: newMessage.recipient,
        recipientName: newMessage.recipient,
        content: newMessage.content,
        timestamp: serverTimestamp(),
        read: false,
        isAdminMessage: true
      });
      setShowMessageForm(false);
      setNewMessage({ recipient: '', content: '' });
      fetchData();
      alert('Message sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message: ' + error.message);
    }
  };

  const handleSaveJob = async () => {
    try {
      // Validate form data
      if (!newJob.title || !newJob.company || !newJob.location) {
        alert('Please fill in all required fields (Title, Company, Location)');
        return;
      }
      
      console.log('Saving job:', newJob);
      
      try {
        // Try to save to Firestore first
        if (editingJob) {
          if (editingJob.id.startsWith('local_')) {
            // If we're editing a local job, just update it locally
            const localJobs = JSON.parse(localStorage.getItem('localJobs') || '[]');
            const updatedLocalJobs = localJobs.map(job => 
              job.id === editingJob.id ? { ...newJob, id: editingJob.id, isLocalOnly: true } : job
            );
            localStorage.setItem('localJobs', JSON.stringify(updatedLocalJobs));
            
            // Update state directly
            setJobs(jobs.map(job => 
              job.id === editingJob.id ? { ...newJob, id: editingJob.id, isLocalOnly: true } : job
            ));
            
            alert('Job updated successfully (stored locally)');
            setShowJobForm(false);
            setEditingJob(null);
            return;
          } else {
            // Try to update in Firestore
            await updateDoc(doc(db, 'jobs', editingJob.id), newJob);
            console.log('Job updated successfully in Firestore');
          }
        } else {
          // Create job with proper timestamp
          const jobData = {
            ...newJob,
            createdAt: new Date().toISOString(),
            createdBy: currentUser.uid
          };
          console.log('Adding new job with data:', jobData);
          
          try {
            const docRef = await addDoc(collection(db, 'jobs'), jobData);
            console.log('Job added to Firestore with ID:', docRef.id);
          } catch (firebaseError) {
            // Automatically fall back to local storage
            throw firebaseError;
          }
        }
      } catch (error) {
        console.error('Firestore error:', error);
        
        // Always use localStorage fallback on any error
        console.log('Using localStorage fallback for job storage');
        
        // Get existing jobs from localStorage
        const localJobs = JSON.parse(localStorage.getItem('localJobs') || '[]');
        
        if (editingJob) {
          // Update existing job in local storage
          const updatedLocalJobs = localJobs.map(job => 
            job.id === editingJob.id ? { ...newJob, id: editingJob.id, isLocalOnly: true } : job
          );
          localStorage.setItem('localJobs', JSON.stringify(updatedLocalJobs));
          
          // Update state
          setJobs(jobs.map(job => 
            job.id === editingJob.id ? { ...newJob, id: editingJob.id, isLocalOnly: true } : job
          ));
        } else {
          // Add new job to local storage
          const newJobWithId = { 
            ...newJob,
            id: 'local_' + Date.now(),
            createdAt: new Date().toISOString(),
            createdBy: currentUser.uid,
            isLocalOnly: true,
            status: newJob.status || 'open'
          };
          
          localJobs.push(newJobWithId);
          localStorage.setItem('localJobs', JSON.stringify(localJobs));
          
          // Add to state
          setJobs([...jobs, newJobWithId]);
        }
        
        alert('Job saved locally. It will be visible to you but not to other users until database permissions are fixed.');
        setShowJobForm(false);
        setEditingJob(null);
        return; // Skip fetchData since we've updated state directly
      }
      
      alert(editingJob ? 'Job updated successfully!' : 'Job posted successfully!');
      setShowJobForm(false);
      setEditingJob(null);
      fetchData();
    } catch (error) {
      console.error('Error saving job:', error);
      
      // Enhanced error handling with more details
      let errorMessage = 'Error saving job.';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'You do not have permission to post jobs. This may be due to insufficient admin privileges in Firestore.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'The Firebase service is currently unavailable. Please try again later.';
      } else if (error.code === 'invalid-argument') {
        errorMessage = 'Invalid data format. Please check your input and try again.';
      }
      
      alert(errorMessage + ' ' + error.message);
    }
  };

  const handleAddMaterial = () => {
    setEditingMaterial(null);
    setNewMaterial({
      title: '',
      subject: '',
      type: 'document',
      url: '',
      description: '',
      tags: []
    });
    setShowMaterialForm(true);
  };

  const handleEditMaterial = (material) => {
    setEditingMaterial(material);
    setNewMaterial({
      title: material.title,
      subject: material.subject,
      type: material.type,
      url: material.url,
      description: material.description,
      tags: material.tags || []
    });
    setShowMaterialForm(true);
  };

  const handleDeleteMaterial = async (materialId) => {
    if (window.confirm('Are you sure you want to delete this material?')) {
      try {
        await deleteDoc(doc(db, 'materials', materialId));
        setMaterials(materials.filter(m => m.id !== materialId));
        alert('Material deleted successfully!');
      } catch (error) {
        console.error('Error deleting material:', error);
        alert('Error: ' + error.message);
      }
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() === '') return;
    setNewMaterial({
      ...newMaterial,
      tags: [...newMaterial.tags, newTag.trim()]
    });
    setNewTag('');
  };

  const handleRemoveTag = (tag) => {
    setNewMaterial({
      ...newMaterial,
      tags: newMaterial.tags.filter(t => t !== tag)
    });
  };

  const handleSaveMaterial = async () => {
    try {
      if (editingMaterial) {
        // Update existing material
        await updateDoc(doc(db, 'materials', editingMaterial.id), newMaterial);
        setMaterials(materials.map(m => 
          m.id === editingMaterial.id ? { ...m, ...newMaterial } : m
        ));
      } else {
        // Add new material
        const materialRef = await addDoc(collection(db, 'materials'), {
          ...newMaterial,
          createdAt: new Date().toISOString(),
          createdBy: currentUser.uid
        });
        
        setMaterials([...materials, { id: materialRef.id, ...newMaterial }]);
      }
      
      // Reset form
      setShowMaterialForm(false);
      setEditingMaterial(null);
      setNewMaterial({
        title: '',
        subject: '',
        type: 'document',
        url: '',
        description: '',
        tags: []
      });
      
      alert(editingMaterial ? 'Material updated successfully!' : 'Material added successfully!');
    } catch (error) {
      console.error('Error saving material:', error);
      alert('Error: ' + error.message);
    }
  };

  const filteredQuestions = questions.filter(q => 
    q.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    q.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredEmails = emails.filter(e => 
    e.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredJobs = jobs.filter(job => 
    job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderDashboard = () => (
    <div className="dashboard-container">
      <div className="dashboard-cards">
        <div className="dashboard-card" onClick={() => setActiveTab('users')}>
          <FaUsers className="card-icon" />
          <h3>User Management</h3>
          <p>{users.length} users</p>
        </div>
        <div className="dashboard-card" onClick={() => setActiveTab('questions')}>
          <FaQuestionCircle className="card-icon" />
          <h3>Q&A Moderation</h3>
          <p>{questions.length} questions</p>
        </div>
        <div className="dashboard-card" onClick={() => setActiveTab('messages')}>
          <FaInbox className="card-icon" />
          <h3>Messaging</h3>
          <p>{messages.length} messages</p>
        </div>
        <div className="dashboard-card" onClick={() => setActiveTab('email')}>
          <FaEnvelope className="card-icon" />
          <h3>Contact Inquiries</h3>
          <p>{emails.length} messages</p>
        </div>
        <div className="dashboard-card" onClick={() => setActiveTab('jobs')}>
          <FaBriefcase className="card-icon" />
          <h3>Job Board</h3>
          <p>{jobs.length} listings</p>
        </div>
        <div className="dashboard-card" onClick={() => setActiveTab('exams')}>
          <FaFileAlt className="card-icon" />
          <h3>Mock Exams</h3>
          <p>{mockExams.length} exams</p>
        </div>
        <div className="dashboard-card" onClick={() => setActiveTab('materials')}>
          <FaGraduationCap className="card-icon" />
          <h3>Learning Materials</h3>
          <p>{materials.length} resources</p>
        </div>
        <div className="dashboard-card" onClick={() => navigate('/admin/digital-library')}>
          <FaBook className="card-icon" />
          <h3>Digital Library</h3>
          <p>Manage academic resources</p>
        </div>
        <div className="dashboard-card" onClick={() => setActiveTab('chatService')}>
          <FaRobot className="card-icon" />
          <h3>Chat Service</h3>
          <p>Manage the AI assistant</p>
        </div>
        <div className="dashboard-card" onClick={() => setActiveTab('moderation')}>
          <FaShieldAlt className="card-icon" />
          <h3>Content Moderation</h3>
          <p>Review reported content</p>
        </div>
        <div className="dashboard-card" onClick={() => setActiveTab('expert-support')}>
          <FaUserGraduate className="card-icon" />
          <h3>Expert Support</h3>
          <p>Manage support requests</p>
        </div>
        <div className="dashboard-card" onClick={() => setActiveTab('settings')}>
          <FaCog className="card-icon" />
          <h3>Settings</h3>
          <p>Configure website settings</p>
        </div>
      </div>
    </div>
  );

  const renderUsersSection = () => (
    <div className="section">
      <div className="section-header">
        <h2>User Management</h2>
        <button className="add-btn" onClick={() => setShowUserForm(true)}>
          <FaPlus /> Add User
        </button>
      </div>
      <div className="users-grid">
        {users.map(user => (
          <div key={user.id} className="user-card">
            <div className="user-header">
              <h3>{user.email}</h3>
              <span className={`role-tag ${user.role}`}>{user.role}</span>
            </div>
            <div className="user-actions">
              <button onClick={() => handleEditUser(user.id)}>
                <FaEdit /> Edit
              </button>
              <button onClick={() => handleDeleteUser(user.id)}>
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderMockExamsSection = () => (
    <div className="section">
      <div className="section-header">
        <h2>Mock Exam Management</h2>
        <button className="add-btn" onClick={handleAddExam}>
          <FaPlus /> Add Exam
        </button>
      </div>
      <div className="exams-grid">
        {mockExams.map(exam => (
          <div key={exam.id} className="exam-card">
            <div className="exam-header">
              <h3>{exam.title}</h3>
              <span className="subject-tag">{exam.subject}</span>
            </div>
            <div className="exam-details">
              <p>Duration: {exam.duration} minutes</p>
              <p>Questions: {exam.questions.length}</p>
              <p>Total Marks: {exam.totalMarks}</p>
            </div>
            <div className="exam-actions">
              <button onClick={() => handleEditExam(exam)}>
                <FaEdit /> Edit
              </button>
              <button onClick={() => handleDeleteExam(exam.id)}>
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderJobsSection = () => (
    <div className="section">
      <div className="section-header">
        <h2>Job Management</h2>
        <div>
          <button className="add-btn" onClick={handleAddJob}>
            <FaPlus /> Post Job
          </button>
        </div>
      </div>
      
      {jobs.length === 0 ? (
        <div className="empty-state">
          <p>No jobs found. Click "Post Job" to add your first job listing.</p>
        </div>
      ) : (
        <>
          {/* Info banner for local jobs */}
          {jobs.some(job => job.isLocalOnly) && (
            <div className="info-banner">
              <p>
                <strong>Note:</strong> Some jobs are stored locally due to database permission issues. 
                These jobs will only be visible to you until database permissions are fixed.
              </p>
            </div>
          )}
          
          <div className="jobs-grid">
            {jobs.map(job => (
              <div key={job.id} className={`job-card ${job.isLocalOnly ? 'local-only' : ''}`}>
                <div className="job-header">
                  <h3>{job.title}</h3>
                  <div className="status-container">
                    {job.isLocalOnly && (
                      <span className="local-badge" title="This job is stored locally and not in Firebase">Local Only</span>
                    )}
                    <span className={`status ${job.status?.toLowerCase()}`}>{job.status || 'Open'}</span>
                  </div>
                </div>
                <div className="job-details">
                  <p><strong>Company:</strong> {job.company}</p>
                  <p><strong>Location:</strong> {job.location}</p>
                  <p><strong>Type:</strong> {job.type}</p>
                  {job.createdAt && (
                    <p className="job-date"><small>Posted: {new Date(job.createdAt).toLocaleDateString()}</small></p>
                  )}
                </div>
                <div className="job-actions">
                  <button onClick={() => handleEditJob(job)}>
                    <FaEdit /> Edit
                  </button>
                  <button onClick={() => handleDeleteJob(job.id)}>
                    <FaTrash /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const renderMessagesSection = () => (
    <div className="section">
      <div className="section-header">
        <h2>Message Management</h2>
        <button className="add-btn" onClick={() => setShowMessageForm(true)}>
          <FaPlus /> New Message
        </button>
      </div>
      <div className="messages-grid">
        {messages.map(message => (
          <div key={message.id} className={`message-card ${message.read ? 'read' : 'unread'}`}>
            <div className="message-header">
              <h3>{message.senderId === 'admin' ? 'To: ' + message.recipientName : 'From: ' + message.senderName}</h3>
              <span className="recipient">{message.senderId === 'admin' ? message.recipientId : message.senderId}</span>
            </div>
            <p className="message-content">{message.content}</p>
            <div className="message-footer">
              <span className="timestamp">
                {message.timestamp?.toDate ? message.timestamp.toDate().toLocaleString() : 
                (message.timestamp ? new Date(message.timestamp).toLocaleString() : 'Unknown')}
              </span>
              <span className={`status ${message.read ? 'read' : 'unread'}`}>
                {message.read ? 'Read' : 'Unread'}
              </span>
            </div>
          </div>
        ))}
      </div>
      {showMessageForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Send Message to User</h3>
            <div className="form-group">
              <label>Recipient User ID</label>
              <input
                type="text"
                value={newMessage.recipient}
                onChange={(e) => setNewMessage({...newMessage, recipient: e.target.value})}
                placeholder="Enter user ID"
              />
            </div>
            <div className="form-group">
              <label>Message</label>
              <textarea
                value={newMessage.content}
                onChange={(e) => setNewMessage({...newMessage, content: e.target.value})}
                placeholder="Enter your message"
                rows={5}
              ></textarea>
            </div>
            <div className="modal-buttons">
              <button onClick={() => setShowMessageForm(false)}>Cancel</button>
              <button onClick={handleSendMessage}>Send</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderChatServiceAdmin = () => (
    <motion.div 
      className="chatgpt-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="section-header">
        <h2>ChatGPT Service Administration</h2>
      </div>
      <ChatServiceAdmin />
    </motion.div>
  );

  const renderModerationAdmin = () => (
    <motion.div 
      className="moderation-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="section-header">
        <h2>AI Content Moderation</h2>
      </div>
      <ModerationAdmin />
    </motion.div>
  );

  const renderExpertSupportAdmin = () => (
    <motion.div 
      className="expert-support-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="section-header">
        <h2>Expert Support</h2>
      </div>
      <ExpertSupportAdmin />
    </motion.div>
  );

  const renderMaterialsSection = () => (
    <div className="section">
      <div className="section-header">
        <h2>Academic Materials</h2>
        <button className="add-btn" onClick={handleAddMaterial}>
          <FaPlus /> Add Material
        </button>
      </div>
      <div className="materials-grid">
        {materials.map(material => (
          <div key={material.id} className="material-card">
            <div className="material-header">
              <h3>{material.title}</h3>
              <span className="material-type">{material.type}</span>
            </div>
            <div className="material-details">
              <p><strong>Subject:</strong> {material.subject}</p>
              <p className="material-description">{material.description}</p>
              <div className="material-tags">
                {material.tags && material.tags.map((tag, index) => (
                  <span key={index} className="tag">{tag}</span>
                ))}
              </div>
            </div>
            <div className="material-actions">
              <a href={material.url} target="_blank" rel="noopener noreferrer" className="view-link">
                <FaEye /> View
              </a>
              <button onClick={() => handleEditMaterial(material)}>
                <FaEdit /> Edit
              </button>
              <button onClick={() => handleDeleteMaterial(material.id)}>
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // If still checking admin status, show loading
  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <>
      <SEO 
        title="Admin Dashboard - Student Helper"
        description="Manage questions, users, emails, and website settings on Student Helper platform."
        keywords="admin dashboard, content management, user management, email management, website settings"
      />
      <div className="admin-container">
        <div className="admin-sidebar">
          <div className="sidebar-header">
            <h2>Admin Dashboard</h2>
          </div>
          <div className="sidebar-menu">
            <button 
              className={`menu-item ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              <FaChartBar className="menu-icon" />
              <span>Dashboard</span>
            </button>
            <button 
              className={`menu-item ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              <FaUsers className="menu-icon" />
              <span>Users</span>
            </button>
            <button 
              className={`menu-item ${activeTab === 'exams' ? 'active' : ''}`}
              onClick={() => setActiveTab('exams')}
            >
              <FaGraduationCap className="menu-icon" />
              <span>Mock Exams</span>
            </button>
            <button 
              className={`menu-item ${activeTab === 'materials' ? 'active' : ''}`}
              onClick={() => setActiveTab('materials')}
            >
              <FaFileAlt className="menu-icon" />
              <span>Academic Materials</span>
            </button>
            <button 
              className={`menu-item ${activeTab === 'jobs' ? 'active' : ''}`}
              onClick={() => setActiveTab('jobs')}
            >
              <FaBriefcase className="menu-icon" />
              <span>Jobs</span>
            </button>
            <button 
              className={`menu-item ${activeTab === 'messages' ? 'active' : ''}`}
              onClick={() => setActiveTab('messages')}
            >
              <FaEnvelope className="menu-icon" />
              <span>Messages</span>
            </button>
            <button 
              className={`menu-item ${activeTab === 'chatgpt' ? 'active' : ''}`}
              onClick={() => setActiveTab('chatgpt')}
            >
              <FaRobot className="menu-icon" />
              <span>ChatGPT</span>
            </button>
            <button 
              className={`menu-item ${activeTab === 'moderation' ? 'active' : ''}`}
              onClick={() => setActiveTab('moderation')}
            >
              <FaShieldAlt className="menu-icon" />
              <span>Content Moderation</span>
            </button>
            <button 
              className={`menu-item ${activeTab === 'expert-support' ? 'active' : ''}`}
              onClick={() => setActiveTab('expert-support')}
            >
              <FaUserGraduate className="menu-icon" />
              <span>Expert Support</span>
            </button>
            <button 
              className={`menu-item ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              <FaCog className="menu-icon" />
              <span>Settings</span>
            </button>
          </div>
        </div>

        <div className="admin-content">
          <div className="content-header">
            <div className="search-bar">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {activeTab === 'jobs' && (
              <button 
                className="add-button"
                onClick={handleAddJob}
              >
                <FaPlus /> Add New Job
              </button>
            )}
          </div>

          {activeTab === 'emails' && (
            <motion.div 
              className="emails-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="section-header">
                <h2>Email Management</h2>
                <button className="filter-btn">
                  <FaFilter />
                  <span>Filter</span>
                </button>
              </div>

              <div className="emails-grid">
                {filteredEmails.map(email => (
                  <motion.div 
                    key={email.id}
                    className={`email-card ${email.read ? 'read' : 'unread'}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="email-header">
                      <h3>{email.subject}</h3>
                      <div className="email-actions">
                        <button className="action-btn reply">
                          <FaPaperPlane />
                        </button>
                        <button className="action-btn delete">
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                    <p className="email-from">From: {email.from}</p>
                    <p className="email-content">{email.content}</p>
                    <div className="email-footer">
                      <span className="date">{email.date}</span>
                      {!email.read && <span className="unread-badge">New</span>}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="send-email-form">
                <h3>Send New Email</h3>
                <form onSubmit={handleSendEmail}>
                  <div className="form-group">
                    <label>To:</label>
                    <input
                      type="email"
                      value={newEmail.to}
                      onChange={(e) => setNewEmail({ ...newEmail, to: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Subject:</label>
                    <input
                      type="text"
                      value={newEmail.subject}
                      onChange={(e) => setNewEmail({ ...newEmail, subject: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Content:</label>
                    <textarea
                      value={newEmail.content}
                      onChange={(e) => setNewEmail({ ...newEmail, content: e.target.value })}
                      required
                    />
                  </div>
                  <button type="submit" className="send-btn">
                    <FaPaperPlane />
                    Send Email
                  </button>
                </form>
              </div>
            </motion.div>
          )}

          {activeTab === 'settings' && (
            <motion.div 
              className="settings-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="section-header">
                <h2>Website Settings</h2>
              </div>

              <div className="settings-grid">
                <div className="setting-card">
                  <h3>Maintenance Mode</h3>
                  <p>Enable maintenance mode to temporarily disable the website</p>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id="maintenanceMode"
                      checked={websiteSettings.maintenanceMode}
                      onChange={(e) => handleUpdateSettings('maintenanceMode', e.target.checked)}
                    />
                    <label htmlFor="maintenanceMode"></label>
                  </div>
                </div>

                <div className="setting-card">
                  <h3>User Registration</h3>
                  <p>Enable or disable new user registration</p>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id="registrationEnabled"
                      checked={websiteSettings.registrationEnabled}
                      onChange={(e) => handleUpdateSettings('registrationEnabled', e.target.checked)}
                    />
                    <label htmlFor="registrationEnabled"></label>
                  </div>
                </div>

                <div className="setting-card">
                  <h3>Questions Limit</h3>
                  <p>Maximum questions a user can post per day</p>
                  <input
                    type="number"
                    value={websiteSettings.maxQuestionsPerDay}
                    onChange={(e) => handleUpdateSettings('maxQuestionsPerDay', parseInt(e.target.value))}
                    min="1"
                    max="100"
                  />
                </div>

                <div className="setting-card">
                  <h3>File Upload Limit</h3>
                  <p>Maximum file size for uploads (MB)</p>
                  <input
                    type="number"
                    value={websiteSettings.maxFileSize}
                    onChange={(e) => handleUpdateSettings('maxFileSize', parseInt(e.target.value))}
                    min="1"
                    max="100"
                  />
                </div>

                <div className="setting-card">
                  <h3>Analytics</h3>
                  <p>Enable website analytics tracking</p>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id="analyticsEnabled"
                      checked={websiteSettings.analyticsEnabled}
                      onChange={(e) => handleUpdateSettings('analyticsEnabled', e.target.checked)}
                    />
                    <label htmlFor="analyticsEnabled"></label>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'dashboard' && renderDashboard()}
          {activeTab === 'users' && renderUsersSection()}
          {activeTab === 'exams' && renderMockExamsSection()}
          {activeTab === 'materials' && renderMaterialsSection()}
          {activeTab === 'jobs' && renderJobsSection()}
          {activeTab === 'messages' && renderMessagesSection()}
          {activeTab === 'chatgpt' && renderChatServiceAdmin()}
          {activeTab === 'moderation' && renderModerationAdmin()}
          {activeTab === 'expert-support' && renderExpertSupportAdmin()}
        </div>
      </div>

      {showUserForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>{editingUser ? 'Edit User' : 'Add User'}</h2>
            <input
              type="email"
              placeholder="Email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            />
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="moderator">Moderator</option>
            </select>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={newUser.isActive}
                  onChange={(e) => setNewUser({ ...newUser, isActive: e.target.checked })}
                />
                Active Account
              </label>
            </div>
            <div className="modal-actions">
              <button onClick={handleAddUser}>Save</button>
              <button onClick={() => setShowUserForm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showExamForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>{editingExam ? 'Edit Exam' : 'Add Exam'}</h2>
            <input
              type="text"
              placeholder="Title"
              value={newExam.title}
              onChange={(e) => setNewExam({ ...newExam, title: e.target.value })}
            />
            <input
              type="text"
              placeholder="Subject"
              value={newExam.subject}
              onChange={(e) => setNewExam({ ...newExam, subject: e.target.value })}
            />
            <input
              type="number"
              placeholder="Duration (minutes)"
              value={newExam.duration}
              onChange={(e) => setNewExam({ ...newExam, duration: parseInt(e.target.value) })}
            />
            <input
              type="number"
              placeholder="Total Marks"
              value={newExam.totalMarks}
              onChange={(e) => setNewExam({ ...newExam, totalMarks: parseInt(e.target.value) })}
            />
            
            <div className="questions-section">
              <h3>Questions</h3>
              {newExam.questions.map((q, index) => (
                <div key={index} className="question-item">
                  <p>{q.question}</p>
                  <div className="options">
                    {q.options.map((opt, optIndex) => (
                      <div key={optIndex} className="option">
                        {opt} {optIndex === q.correctAnswer && <FaCheck />}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              <div className="add-question-form">
                <input
                  type="text"
                  placeholder="Question"
                  value={newQuestion.question}
                  onChange={(e) => setNewQuestion({ ...newQuestion, question: e.target.value })}
                />
                {newQuestion.options.map((opt, index) => (
                  <input
                    key={index}
                    type="text"
                    placeholder={`Option ${index + 1}`}
                    value={opt}
                    onChange={(e) => {
                      const newOptions = [...newQuestion.options];
                      newOptions[index] = e.target.value;
                      setNewQuestion({ ...newQuestion, options: newOptions });
                    }}
                  />
                ))}
                <select
                  value={newQuestion.correctAnswer}
                  onChange={(e) => setNewQuestion({ ...newQuestion, correctAnswer: parseInt(e.target.value) })}
                >
                  {newQuestion.options.map((_, index) => (
                    <option key={index} value={index}>Option {index + 1}</option>
                  ))}
                </select>
                <button onClick={handleAddQuestion}>Add Question</button>
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={handleSaveExam}>Save</button>
              <button onClick={() => setShowExamForm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showJobForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>{editingJob ? 'Edit Job' : 'Post Job'}</h2>
            <input
              type="text"
              placeholder="Title"
              value={newJob.title}
              onChange={(e) => setNewJob({ ...newJob, title: e.target.value })}
            />
            <input
              type="text"
              placeholder="Company"
              value={newJob.company}
              onChange={(e) => setNewJob({ ...newJob, company: e.target.value })}
            />
            <input
              type="text"
              placeholder="Location"
              value={newJob.location}
              onChange={(e) => setNewJob({ ...newJob, location: e.target.value })}
            />
            <select
              value={newJob.type}
              onChange={(e) => setNewJob({ ...newJob, type: e.target.value })}
            >
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Contract">Contract</option>
              <option value="Internship">Internship</option>
            </select>
            <textarea
              placeholder="Description"
              value={newJob.description}
              onChange={(e) => setNewJob({ ...newJob, description: e.target.value })}
            />
            <select
              value={newJob.status}
              onChange={(e) => setNewJob({ ...newJob, status: e.target.value })}
            >
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
            <div className="modal-actions">
              <button onClick={handleSaveJob}>Save</button>
              <button onClick={() => setShowJobForm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showMaterialForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>{editingMaterial ? 'Edit Material' : 'Add Material'}</h2>
            <input
              type="text"
              placeholder="Title"
              value={newMaterial.title}
              onChange={(e) => setNewMaterial({ ...newMaterial, title: e.target.value })}
            />
            <input
              type="text"
              placeholder="Subject"
              value={newMaterial.subject}
              onChange={(e) => setNewMaterial({ ...newMaterial, subject: e.target.value })}
            />
            <select
              value={newMaterial.type}
              onChange={(e) => setNewMaterial({ ...newMaterial, type: e.target.value })}
            >
              <option value="document">Document</option>
              <option value="video">Video</option>
              <option value="quiz">Quiz</option>
              <option value="presentation">Presentation</option>
              <option value="link">External Link</option>
            </select>
            <input
              type="text"
              placeholder="URL or Resource Link"
              value={newMaterial.url}
              onChange={(e) => setNewMaterial({ ...newMaterial, url: e.target.value })}
            />
            <textarea
              placeholder="Description"
              value={newMaterial.description}
              onChange={(e) => setNewMaterial({ ...newMaterial, description: e.target.value })}
              rows={3}
            />
            <div className="tags-input">
              <div className="input-group">
                <input
                  type="text"
                  placeholder="Add a tag"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                />
                <button type="button" onClick={handleAddTag} disabled={newTag.trim() === ''}>
                  <FaPlus />
                </button>
              </div>
              <div className="tags-container">
                {newMaterial.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)}>
                      <FaTimes />
                    </button>
                  </span>
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={handleSaveMaterial}>Save</button>
              <button onClick={() => setShowMaterialForm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Admin; 